import { Controller } from "@hotwired/stimulus"

// TinyMCE on a textarea: the same editor the DPK bio uses (owner ruling
// 2026-09-16: "a full text editor just like we have in the edit bio section
// of the DPK"). ONE configuration for every box on the Write screen (owner
// ruling 2026-09-16, evening: "The edit tools should be identical", the
// boxes "identical in size", the body's box 25% taller than it was). The
// presets differ only in how the type sits: the body and the left column
// justified, a right-column block centered, as the document renders them.
//
// The CDN script loads once per page behind a shared promise. Every change
// is written back to the textarea and announced as a bubbling "input"
// event, because the builder's live preview and its save-before-action
// both read the form's fields.
const CDN = "https://cdnjs.cloudflare.com/ajax/libs/tinymce/6.8.5/tinymce.min.js"
let loading = null

const TYPE = "body { font-family: Arial, sans-serif; font-size: 15px; color: #23496d; line-height: 1.75; } a { color: #00a4bd; } h1 { font-size: 18px; line-height: 1.25; }"

const EDITOR = {
  height: 525,
  toolbar: "undo redo | bold italic underline strikethrough | fontfamily fontsize | forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist | link | removeformat",
  font_family_formats: "Arial=arial,helvetica,sans-serif; Georgia=georgia,serif; Times New Roman=times new roman,times,serif; Verdana=verdana,geneva,sans-serif; Courier New=courier new,courier,monospace",
  font_size_formats: "10px 12px 14px 15px 16px 18px 20px 24px 28px 32px"
}

const PRESETS = {
  body: { ...EDITOR, placeholder: "Paste from Google Docs, or start typing.", content_style: `${TYPE} body { text-align: justify; }` },
  left: { ...EDITOR, placeholder: "Text under the body.", content_style: `${TYPE} body { text-align: justify; }` },
  block: { ...EDITOR, placeholder: "A stream line, tour dates, a track list.", content_style: `${TYPE} body { text-align: center; }` }
}

const PLAYER_HOSTS = /open\.spotify\.com|youtube\.com|youtu\.be|music\.apple\.com/i

function loadTinyMCE() {
  if (window.tinymce) return Promise.resolve(window.tinymce)
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.src = CDN
      script.referrerPolicy = "origin"
      script.onload = () => resolve(window.tinymce)
      script.onerror = () => reject(new Error("TinyMCE did not load"))
      document.head.appendChild(script)
    })
  }
  return loading
}

export default class extends Controller {
  static targets = ["textarea"]
  static values = { preset: { type: String, default: "body" } }

  async connect() {
    let tinymce
    try {
      tinymce = await loadTinyMCE()
    } catch (e) {
      // The plain textarea still works; the form still submits.
      return
    }
    if (!this.element.isConnected) return
    const preset = PRESETS[this.presetValue] || PRESETS.body
    tinymce.init({
      target: this.textareaTarget,
      menubar: false,
      branding: false,
      promotion: false,
      plugins: "lists link",
      // Every button on screen (owner ruling 2026-09-16); the default
      // "floating" mode hid the second half behind a three-dot button.
      toolbar_mode: "wrap",
      // A Chrome paste from Google Docs carries a font and a colour on every
      // span; keep only bold, italic and underline from the clipboard so the
      // template's type stays the template's.
      paste_webkit_styles: "font-weight font-style text-decoration",
      // A player pasted into a text box would be stripped silently by the
      // sanitizer; say so and point at the Player button instead.
      paste_preprocess: (editor, args) => {
        if (/<iframe/i.test(args.content) && PLAYER_HOSTS.test(args.content)) {
          args.content = ""
          if (window.showToast) window.showToast("error", "Players are their own block. Use + Player under the column.")
        }
      },
      ...preset,
      setup: (editor) => {
        this.editor = editor
        editor.on("change input keyup undo redo", () => {
          editor.save()
          this.textareaTarget.dispatchEvent(new Event("input", { bubbles: true }))
        })
      }
    })
  }

  disconnect() {
    if (this.editor && window.tinymce) {
      window.tinymce.remove(this.editor)
      this.editor = null
    }
  }
}
